/**
 * @file Customer detail view — full customer info, jobs, notes, activity
 */

import {
  getCustomerById, updateCustomer, addCustomerNote,
  getCustomerJobs, getCustomerRevenue, timeAgo
} from '@services/customers.js';
import { pop, push } from '@core/router.js';
import { toast } from '@components/toast.js';
import { showModal, closeModal } from '@components/modal.js';
import { renderJobCard } from '@components/job-card.js';

function render({ customerId }) {
  const cust = getCustomerById(customerId);
  if (!cust) {
    return `
      <button class="back-btn" id="detail-back">\u2190 Back</button>
      <div class="empty-state">
        <div class="empty-state-icon">\u26A0\uFE0F</div>
        <div class="empty-state-text">Customer not found</div>
      </div>
    `;
  }

  const displayName = cust.name || `${cust.firstName || ''} ${cust.lastName || ''}`.trim() || 'Unnamed';
  const jobs = getCustomerJobs(customerId);
  const revenue = getCustomerRevenue(customerId);
  const notes = cust.contactNotes || [];
  const activities = (cust.activities || []).slice().reverse();

  return `
    <button class="back-btn" id="detail-back">\u2190 Back</button>

    <div class="card mb-md">
      <h2 style="margin-bottom:4px;">${esc(displayName)}</h2>
      ${cust.companyName ? `<div class="text-muted text-sm">${esc(cust.companyName)}</div>` : ''}
      <button class="btn btn-sm btn-outline" id="edit-customer-btn" style="margin-top:8px;">Edit</button>
    </div>

    <!-- Contact Section -->
    <div class="detail-section">
      <div class="detail-section-header">Contact</div>
      <div class="detail-section-content">
        ${cust.phone ? `
          <div class="lead-contact-row">
            <a href="tel:${esc(cust.phone)}" class="lead-contact-link lead-contact-call">
              <span class="lead-contact-icon">\uD83D\uDCDE</span>
              <span class="lead-contact-text">${esc(cust.phone)}</span>
            </a>
            <a href="sms:${esc(cust.phone)}" class="lead-contact-action" title="Text">
              \u2709\uFE0F
            </a>
          </div>
        ` : ''}
        ${cust.email ? `
          <div class="lead-contact-row">
            <a href="mailto:${esc(cust.email)}" class="lead-contact-link">
              <span class="lead-contact-icon">\u2709</span>
              <span class="lead-contact-text">${esc(cust.email)}</span>
            </a>
          </div>
        ` : ''}
        ${cust.address ? `
          <div class="lead-contact-row">
            <a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cust.address)}" target="_blank" class="lead-contact-link">
              <span class="lead-contact-icon">\uD83D\uDCCD</span>
              <span class="lead-contact-text">${esc(cust.address)}</span>
            </a>
          </div>
        ` : ''}
        <div class="lead-contact-row" style="color:var(--gray-400);font-size:0.78rem;">
          Last contacted: ${timeAgo(cust.lastContacted)}
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions" id="cust-quick-actions">
      ${cust.phone ? `<a href="tel:${esc(cust.phone)}" class="quick-action"><span class="qa-icon">\uD83D\uDCDE</span>Call</a>` : ''}
      ${cust.phone ? `<a href="sms:${esc(cust.phone)}" class="quick-action"><span class="qa-icon">\u2709\uFE0F</span>Text</a>` : ''}
      ${cust.email ? `<a href="mailto:${esc(cust.email)}" class="quick-action"><span class="qa-icon">\u2709</span>Email</a>` : ''}
      ${cust.address ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cust.address)}" target="_blank" class="quick-action"><span class="qa-icon">\uD83D\uDCCD</span>Directions</a>` : ''}
    </div>

    <!-- Stats -->
    <div class="stats-grid" style="margin-bottom:16px;">
      <div class="stat-card">
        <div class="stat-value">$${revenue.toLocaleString()}</div>
        <div class="stat-label">Total Revenue</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${jobs.length}</div>
        <div class="stat-label">Jobs</div>
      </div>
    </div>

    <!-- Jobs Section -->
    <div class="detail-section">
      <div class="detail-section-header">Jobs (${jobs.length})</div>
      <div class="detail-section-content" id="cust-jobs-list">
        ${jobs.length > 0
          ? jobs.map(j => renderJobCard(j)).join('')
          : '<div class="text-muted text-sm">No jobs yet</div>'
        }
      </div>
    </div>

    <!-- Notes Section -->
    <div class="detail-section">
      <div class="detail-section-header">
        Notes (${notes.length})
        <button class="btn btn-sm btn-outline" id="add-cust-note-btn">+ Add</button>
      </div>
      <div class="detail-section-content" id="cust-notes-list">
        ${notes.length > 0
          ? notes.slice().reverse().map(n => `
              <div class="note-item">
                <div class="note-text">${esc(n.text)}</div>
                <div class="note-date">${formatDate(n.date)}</div>
              </div>
            `).join('')
          : '<div class="text-muted text-sm">No notes yet</div>'
        }
      </div>
    </div>

    <!-- Activity Timeline -->
    ${activities.length > 0 ? `
    <div class="detail-section">
      <div class="detail-section-header">Activity</div>
      <div class="detail-section-content">
        ${activities.slice(0, 20).map(a => `
          <div class="lead-activity-item">
            <div class="lead-activity-text">${esc(a.type || 'Activity')}: ${esc(a.text || '')}</div>
            <div class="lead-activity-time">${formatDate(a.date)}</div>
          </div>
        `).join('')}
      </div>
    </div>
    ` : ''}
  `;
}

function mount({ customerId }) {
  // Back button
  document.getElementById('detail-back')?.addEventListener('click', () => pop());

  // Edit customer
  document.getElementById('edit-customer-btn')?.addEventListener('click', () => {
    const cust = getCustomerById(customerId);
    if (!cust) return;
    const displayName = cust.name || `${cust.firstName || ''} ${cust.lastName || ''}`.trim();

    showModal('Edit Customer', `
      <div class="form-group">
        <label>Name</label>
        <input type="text" id="edit-cust-name" value="${esc(displayName)}" />
      </div>
      <div class="form-group">
        <label>Phone</label>
        <input type="tel" id="edit-cust-phone" value="${esc(cust.phone || '')}" />
      </div>
      <div class="form-group">
        <label>Email</label>
        <input type="email" id="edit-cust-email" value="${esc(cust.email || '')}" />
      </div>
      <div class="form-group">
        <label>Address</label>
        <input type="text" id="edit-cust-address" value="${esc(cust.address || '')}" />
      </div>
    `, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Save', type: 'gold', onClick: () => {
        updateCustomer(customerId, {
          name: document.getElementById('edit-cust-name')?.value?.trim() || displayName,
          phone: document.getElementById('edit-cust-phone')?.value?.trim() || '',
          email: document.getElementById('edit-cust-email')?.value?.trim() || '',
          address: document.getElementById('edit-cust-address')?.value?.trim() || '',
        });
        closeModal();
        toast('Customer updated', 'success');
        rerender(customerId);
      }},
    ]);
  });

  // Add note
  document.getElementById('add-cust-note-btn')?.addEventListener('click', () => {
    showModal('Add Note', `
      <div class="form-group">
        <textarea id="new-cust-note-text" rows="3" placeholder="Enter note..."></textarea>
      </div>
    `, [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Save', type: 'gold', onClick: () => {
        const text = document.getElementById('new-cust-note-text')?.value?.trim();
        if (!text) return;
        addCustomerNote(customerId, text);
        closeModal();
        toast('Note added', 'success');
        rerender(customerId);
      }},
    ]);
    setTimeout(() => document.getElementById('new-cust-note-text')?.focus(), 200);
  });

  // Job card taps
  const jobsList = document.getElementById('cust-jobs-list');
  if (jobsList) {
    jobsList.addEventListener('click', (e) => {
      const card = e.target.closest('.job-card');
      if (card) push('job-detail', { jobId: card.dataset.jobId });
    });
  }
}

function rerender(customerId) {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render({ customerId });
    mount({ customerId });
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) { return ''; }
}

export default { render, mount, unmount };
